import { useRef, useState } from 'react';
import { useEditorStore, type EditorFile, type Language } from '../../store/editorStore';
import styles from './FileExplorer.module.css';

const LANG_ICON: Record<Language, string> = {
    python: '🐍',
    cpp: '⚙️',
};

export function FileExplorer() {
    const { files, activeFileId, closeFile, newFile, setActive } = useEditorStore();

    const handleNew = (lang: Language) => {
        const ext = lang === 'python' ? '.py' : '.cpp';
        const taken = new Set(files.map(f => f.name));

        let raw = window.prompt(`New ${lang} file name:`, `main${ext}`);
        if (raw === null) return; // cancelled

        raw = raw.trim();
        if (!raw) return;

        // Ensure correct extension
        if (!raw.endsWith(ext)) raw += ext;

        // Deduplicate if name already taken
        let name = raw;
        const base = raw.slice(0, -ext.length);
        let n = 1;
        while (taken.has(name)) name = `${base}${n++}${ext}`;

        newFile(name, lang);
    };

    return (
        <div className={styles.explorer}>
            <div className="panel-header" style={{ borderRadius: 0 }}>
                Files
                <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn--ghost btn--icon" onClick={() => handleNew('python')} data-tooltip="New Python file">+🐍</button>
                    <button className="btn btn--ghost btn--icon" onClick={() => handleNew('cpp')} data-tooltip="New C++ file">+⚙️</button>
                </div>
            </div>

            <div className={styles.explorerBody}>
                {files.length === 0 ? (
                    <div className={styles.explorerEmpty}>
                        No files open.<br />Create a new file to start.
                    </div>
                ) : (
                    files.map(f => (
                        <FileItem
                            key={f.id}
                            file={f}
                            active={f.id === activeFileId}
                            onSelect={() => setActive(f.id)}
                            onClose={() => closeFile(f.id)}
                            takenNames={new Set(files.filter(x => x.id !== f.id).map(x => x.name))}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

interface FileItemProps {
    file: EditorFile;
    active: boolean;
    onSelect: () => void;
    onClose: () => void;
    takenNames: Set<string>;
}

function FileItem({ file, active, onSelect, onClose, takenNames }: FileItemProps) {
    const { renameFile } = useEditorStore();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(file.name);
    const inputRef = useRef<HTMLInputElement>(null);

    const ext = file.language === 'python' ? '.py' : '.cpp';

    const startEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDraft(file.name);
        setEditing(true);
        setTimeout(() => {
            inputRef.current?.focus();
            // Select just the base name, not the extension
            const end = file.name.lastIndexOf('.');
            inputRef.current?.setSelectionRange(0, end > 0 ? end : file.name.length);
        }, 0);
    };

    const commit = () => {
        let name = draft.trim();
        if (!name) { cancel(); return; }
        if (!name.endsWith(ext)) name += ext;

        if (name !== file.name) {
            if (takenNames.has(name)) {
                alert(`"${name}" is already taken.`);
                inputRef.current?.focus();
                return;
            }
            renameFile(file.id, name);
        }
        setEditing(false);
    };

    const cancel = () => {
        setDraft(file.name);
        setEditing(false);
    };

    return (
        <div
            className={`${styles.explorerItem} ${active ? styles.explorerItemActive : ''}`}
            onClick={onSelect}
        >
            <span className={styles.explorerItemIcon}>{LANG_ICON[file.language]}</span>

            {editing ? (
                <input
                    ref={inputRef}
                    className={styles.explorerItemInput}   // add minimal styling in your CSS
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') cancel();
                        e.stopPropagation();
                    }}
                    onClick={e => e.stopPropagation()}
                />
            ) : (
                <span
                    className={styles.explorerItemName}
                    onDoubleClick={startEdit}
                    title="Double-click to rename"
                >
                    {file.name}
                </span>
            )}

            {file.dirty && <span className={styles.explorerItemDirty} />}
            <button
                className="btn btn--icon"
                style={{ fontSize: 10, opacity: 0.6, padding: '2px', background: 'none', border: 'none' }}
                onClick={e => { e.stopPropagation(); onClose(); }}
                data-tooltip="Close"
            >✕</button>
        </div>
    );
}